const BASE = '/api';

function authHeaders(token: string) {
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

export interface AuthUser {
  id: string;
  email: string;
  nombre: string;
  rol: string;
  institucionId: string | null;
}

export interface Institucion {
  id: string;
  slug: string;
  nombre: string;
  icon: string;
  color: string;
  categorias: string[] | null;
}

export interface Categoria {
  id: string;
  slug: string;
  label: string;
  icon: string;
  color: string;
  subcategorias: string[];
}

export interface Pin {
  id: string;
  label: string;
  brigadista: string;
  lat: number;
  lng: number;
  categoria: Categoria;
  createdAt: string;
}

export interface Reporte {
  id: string;
  titulo: string;
  descripcion?: string;
  estado: 'pendiente' | 'proceso' | 'resuelto';
  prioridad: 'alta' | 'media' | 'baja';
  sector: string;
  brigadista: string;
  categoria: Categoria;
  createdAt: string;
  updatedAt: string;
}

export interface Parroquia {
  id: string;
  nombre: string;
  tipo: string;
  hab: string;
  circuitos: number;
  cobertura: number;
  color: string;
  activos: number;
}

export interface ElementoPlano {
  id: string;
  tipo: string;
  nombre: string;
  sub: string;
  estado: string;
  icBg: string;
  icColor: string;
}

export interface ModeloDrone {
  id: string;
  nombre: string;
  descripcion?: string;
  edificio: string;
  formato: 'b3dm' | 'obj' | 'ply' | 'desconocido';
  dirPath: string;
  fileSize: string;
  subidoPor?: { nombre: string };
  createdAt: string;
  updatedAt: string;
}

export interface Ajuste {
  nocturno: boolean;
  edif3d: boolean;
  limiteParroquia: boolean;
  alertaVial: boolean;
  nuevosRegistros: boolean;
  resumenSemanal: boolean;
}

export interface Vivienda {
  id: string;
  proyecto: string;
  direccion: string;
  bloque?: string;
  piso?: string;
  apartamento?: string;
  lat?: number;
  lng?: number;
  estado: 'construccion' | 'lista' | 'entregada';
  benefNombre?: string;
  benefCedula?: string;
  benefTelefono?: string;
  benefFamilias?: number;
  fechaEntrega?: string;
  registradoPor?: { nombre: string };
  createdAt: string;
  updatedAt: string;
}

async function get<T>(path: string, token: string, params?: Record<string, string>): Promise<T> {
  const url = new URL(BASE + path, window.location.origin);
  if (params) Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
  const r = await fetch(url.toString(), { headers: authHeaders(token) });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

async function patch<T>(path: string, token: string, body: unknown): Promise<T> {
  const r = await fetch(BASE + path, { method: 'PATCH', headers: authHeaders(token), body: JSON.stringify(body) });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

async function post<T>(path: string, token: string, body: unknown): Promise<T> {
  const r = await fetch(BASE + path, { method: 'POST', headers: authHeaders(token), body: JSON.stringify(body) });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export interface UsuarioAdmin {
  id: string;
  email: string;
  nombre: string;
  rol: string;
  institucionId: string | null;
  createdAt: string;
  institucion?: { id: string; slug: string; nombre: string } | null;
}

async function del<T>(path: string, token: string): Promise<T> {
  const r = await fetch(BASE + path, { method: 'DELETE', headers: authHeaders(token) });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

export const api = {
  login: async (email: string, password: string): Promise<{ token: string; user: AuthUser }> => {
    const r = await fetch(`${BASE}/auth/login`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!r.ok) { const e = await r.json(); throw new Error(e.error ?? 'Error al iniciar sesión'); }
    return r.json();
  },
  me: (token: string) => get<AuthUser & { institucion: Institucion | null }>('/auth/me', token),
  instituciones: (token: string) => get<Institucion[]>('/instituciones', token),
  categorias: (token: string) => get<Categoria[]>('/categorias', token),
  pins: (token: string, institucionId?: string) =>
    get<Pin[]>('/pins', token, institucionId ? { institucionId } : undefined),
  parroquias: (token: string) => get<Parroquia[]>('/parroquias', token),
  plano: (token: string, institucionId?: string) =>
    get<ElementoPlano[]>('/plano', token, institucionId ? { institucionId } : undefined),
  reportes: (token: string, params?: Record<string, string>) =>
    get<Reporte[]>('/reportes', token, params),
  ciclarReporte: (token: string, id: string) => patch<Reporte>(`/reportes/${id}/ciclo`, token, {}),
  crearReporte: (token: string, body: {
    titulo: string; categoriaSlug: string; sector: string; brigadista: string;
    prioridad?: string; descripcion?: string; lat?: number; lng?: number;
  }) => post<Reporte>('/reportes', token, body),
  ajustes: (token: string) => get<Ajuste>('/ajustes', token),
  patchAjustes: (token: string, data: Partial<Ajuste>) => patch<Ajuste>('/ajustes', token, data),
  modelos: (token: string) => get<ModeloDrone[]>('/modelos', token),
  eliminarModelo: async (token: string, id: string) => {
    const r = await fetch(`${BASE}/modelos/${id}`, { method: 'DELETE', headers: authHeaders(token) });
    if (!r.ok) throw new Error(await r.text());
    return r.json();
  },
  viviendas: (token: string, params?: Record<string, string>) =>
    get<Vivienda[]>('/viviendas', token, params),
  crearVivienda: (token: string, body: Partial<Vivienda>) =>
    post<Vivienda>('/viviendas', token, body),
  patchVivienda: (token: string, id: string, body: Partial<Vivienda>) =>
    patch<Vivienda>(`/viviendas/${id}`, token, body),
  // Usuarios (admin)
  usuarios: (token: string) => get<UsuarioAdmin[]>('/usuarios', token),
  crearUsuario: (token: string, body: { email: string; password: string; nombre: string; rol: string; institucionId?: string | null }) =>
    post<UsuarioAdmin>('/usuarios', token, body),
  patchUsuario: (token: string, id: string, body: Partial<{ nombre: string; email: string; password: string; rol: string; institucionId: string | null }>) =>
    patch<UsuarioAdmin>(`/usuarios/${id}`, token, body),
  eliminarUsuario: (token: string, id: string) => del<{ ok: boolean }>(`/usuarios/${id}`, token),
};
